import React, { useEffect, useRef, useState } from "react";
import { User, Lock, LogOut, Camera, Loader2, ShieldCheck, Mail, Phone, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const KIND_LABEL = { super_admin: "Founder / Admin", employee: "Staff", agent: "Agent", teacher: "Teacher", student: "Student", parent: "Parent" };

const resizeImage = (file, max = 320) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width: w, height: h } = img;
      if (w > h) { if (w > max) { h = (h * max) / w; w = max; } }
      else { if (h > max) { w = (w * max) / h; h = max; } }
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

export default function AccountMenu() {
  const { user, setUser, logout } = useAuth();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [dialCodes, setDialCodes] = useState([]);

  // email change
  const [newEmail, setNewEmail] = useState("");
  const [emailStage, setEmailStage] = useState("idle"); // idle | code
  const [emailOtp, setEmailOtp] = useState("");
  const [emailDemo, setEmailDemo] = useState("");
  // phone change
  const [newDial, setNewDial] = useState(user?.dial_code || "+60");
  const [newPhone, setNewPhone] = useState("");
  const [phoneStage, setPhoneStage] = useState("idle");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneDemo, setPhoneDemo] = useState("");
  // password
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });

  useEffect(() => { setName(user?.name || ""); setNewDial(user?.dial_code || "+60"); }, [user]);
  useEffect(() => { api.get("/auth/dial-codes").then((r) => setDialCodes(r.data)).catch(() => {}); }, []);

  if (!user) return null;
  const initial = (user.name || "?").slice(0, 1).toUpperCase();

  const openProfile = () => {
    setNewEmail(""); setEmailStage("idle"); setEmailOtp(""); setEmailDemo("");
    setNewPhone(""); setPhoneStage("idle"); setPhoneOtp(""); setPhoneDemo("");
    setName(user.name || ""); setProfileOpen(true);
  };

  const uploadPhoto = async (e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast({ title: "Please choose an image", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const avatar = await resizeImage(file);
      const { data } = await api.put("/auth/profile", { avatar });
      setUser(data); toast({ title: "Profile photo updated" });
    } catch (err) { toast({ title: "Upload failed", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const saveName = async () => {
    setBusy(true);
    try { const { data } = await api.put("/auth/profile", { name }); setUser(data); toast({ title: "Name updated" }); }
    catch (err) { toast({ title: "Update failed", description: err?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const sendEmailCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email/request", { new_email: newEmail });
      setEmailStage("code"); setEmailDemo(data.demo_otp || "");
      toast({ title: data.email_sent ? `Code sent to ${newEmail}` : "Verification code generated" });
    } catch (err) { toast({ title: "Could not send code", description: err?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const verifyEmail = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/change-email/verify", { otp: emailOtp }); setUser(data); setEmailStage("idle"); setNewEmail(""); setEmailOtp(""); toast({ title: "Email updated & verified" }); }
    catch (err) { toast({ title: "Verification failed", description: err?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const sendPhoneCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-phone/request", { dial_code: newDial, phone: newPhone });
      setPhoneStage("code"); setPhoneDemo(data.demo_otp || "");
      toast({ title: data.sms_sent ? `Code sent to ${data.sent_to}` : "Verification code generated" });
    } catch (err) { toast({ title: "Could not send code", description: err?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const verifyPhone = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/change-phone/verify", { otp: phoneOtp }); setUser(data); setPhoneStage("idle"); setNewPhone(""); setPhoneOtp(""); toast({ title: "Phone updated & verified" }); }
    catch (err) { toast({ title: "Verification failed", description: err?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const savePassword = async () => {
    if (pwd.next.length < 6) { toast({ title: "New password must be at least 6 characters", variant: "destructive" }); return; }
    if (pwd.next !== pwd.confirm) { toast({ title: "Passwords do not match", variant: "destructive" }); return; }
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next }); setPwd({ current: "", next: "", confirm: "" }); setPwdOpen(false); toast({ title: "Password changed" }); }
    catch (err) { toast({ title: "Change failed", description: err?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const Avatar = ({ size = 40 }) => (
    user.avatar
      ? <img src={user.avatar} alt={user.name} className="rounded-full object-cover" style={{ width: size, height: size }} />
      : <div className="rounded-full bg-[#004030] text-[#e0c05a] grid place-items-center font-bold" style={{ width: size, height: size }}>{initial}</div>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-3 outline-none" data-testid="account-menu-trigger">
            <div className="text-right hidden sm:block">
              <p className="font-semibold text-[#012a1e] text-sm">{user.name}</p>
              <p className="text-xs text-[#c9a227] font-medium">{KIND_LABEL[user.kind]}{(user.agent_code || user.student_code) ? ` · ${user.agent_code || user.student_code}` : ""}</p>
            </div>
            <Avatar />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="truncate">
            {user.email}
            {(user.agent_code || user.student_code) && <span className="block font-mono text-[11px] text-[#004030] mt-0.5" data-testid="account-code">{user.agent_code || user.student_code}</span>}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={openProfile} data-testid="menu-edit-profile"><User size={15} className="mr-2" /> Profile &amp; photo</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="menu-change-password"><Lock size={15} className="mr-2" /> Change password</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600 focus:text-red-700"><LogOut size={15} className="mr-2" /> Log out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Profile & photo dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader><DialogTitle className="font-display">Profile &amp; Photo</DialogTitle></DialogHeader>
          <div className="space-y-5 py-1">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar size={72} />
                <button onClick={() => fileRef.current?.click()} disabled={busy} className="absolute -bottom-1 -right-1 bg-[#004030] text-white rounded-full w-7 h-7 grid place-items-center shadow" data-testid="change-photo-btn" title="Change photo"><Camera size={14} /></button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto} data-testid="photo-input" />
              </div>
              <div className="text-sm text-slate-500">Upload a square photo. It'll be resized automatically.</div>
            </div>

            <div className="space-y-1.5">
              <Label>Full name</Label>
              <div className="flex gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="profile-name" />
                <Button onClick={saveName} disabled={busy || !name.trim()} className="bg-[#004030] hover:bg-[#013d2b] text-white" data-testid="save-name">Save</Button>
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5 border-t border-slate-100 pt-4">
              <Label className="flex items-center gap-1.5"><Mail size={14} /> Email</Label>
              <p className="text-sm text-slate-600 flex items-center gap-1.5">{user.email}{user.email_verified && <ShieldCheck size={14} className="text-green-600" />}</p>
              {emailStage === "idle" ? (
                <div className="flex gap-2 mt-1">
                  <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="New email address" className="h-11" data-testid="new-email" />
                  <Button onClick={sendEmailCode} disabled={busy || !newEmail.includes("@")} variant="outline" className="border-[#004030] text-[#004030]" data-testid="send-email-code">Send code</Button>
                </div>
              ) : (
                <div className="bg-[#f7faf8] rounded-xl p-3 mt-1 space-y-2">
                  <p className="text-xs text-slate-500">Enter the 6-digit code sent to <b>{newEmail}</b>.{emailDemo && <span className="ml-1 text-[#c9a227] font-semibold">Demo code: {emailDemo}</span>}</p>
                  <div className="flex gap-2">
                    <Input value={emailOtp} onChange={(e) => setEmailOtp(e.target.value)} placeholder="Code" className="h-11" data-testid="email-otp" />
                    <Button onClick={verifyEmail} disabled={busy || !emailOtp} className="bg-[#004030] hover:bg-[#013d2b] text-white" data-testid="verify-email"><Check size={15} className="mr-1" /> Verify</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-1.5 border-t border-slate-100 pt-4">
              <Label className="flex items-center gap-1.5"><Phone size={14} /> Phone</Label>
              <p className="text-sm text-slate-600 flex items-center gap-1.5">{user.dial_code} {user.phone || "—"}{user.phone_verified && <ShieldCheck size={14} className="text-green-600" />}</p>
              {phoneStage === "idle" ? (
                <div className="flex gap-2 mt-1">
                  <select value={newDial} onChange={(e) => setNewDial(e.target.value)} className="h-11 rounded-md border border-input bg-background px-2 text-sm w-24" data-testid="new-dial">
                    {dialCodes.map((d) => <option key={d.code} value={d.dial}>{d.flag} {d.dial}</option>)}
                  </select>
                  <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="New phone number" className="h-11" data-testid="new-phone" />
                  <Button onClick={sendPhoneCode} disabled={busy || !newPhone.trim()} variant="outline" className="border-[#004030] text-[#004030] whitespace-nowrap" data-testid="send-phone-code">Send code</Button>
                </div>
              ) : (
                <div className="bg-[#f7faf8] rounded-xl p-3 mt-1 space-y-2">
                  <p className="text-xs text-slate-500">Enter the code sent to <b>{newDial} {newPhone}</b>.{phoneDemo && <span className="ml-1 text-[#c9a227] font-semibold">Demo code: {phoneDemo}</span>}</p>
                  <div className="flex gap-2">
                    <Input value={phoneOtp} onChange={(e) => setPhoneOtp(e.target.value)} placeholder="Code" className="h-11" data-testid="phone-otp" />
                    <Button onClick={verifyPhone} disabled={busy || !phoneOtp} className="bg-[#004030] hover:bg-[#013d2b] text-white" data-testid="verify-phone"><Check size={15} className="mr-1" /> Verify</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setProfileOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="max-w-md" data-testid="password-dialog">
          <DialogHeader><DialogTitle className="font-display">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className="h-11" data-testid="current-password" /></div>
            <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className="h-11" data-testid="new-password" /></div>
            <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="h-11" data-testid="confirm-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)}>Cancel</Button>
            <Button onClick={savePassword} disabled={busy} className="bg-[#004030] hover:bg-[#013d2b] text-white" data-testid="save-password">{busy ? <Loader2 className="animate-spin" size={16} /> : "Update password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
